// @flow

import Honeybadger from 'honeybadger-js';
import type { JSONValue } from 'common/types';

let failureStack: null | Array<string> = null;
// Wrap root-level extraction with this higher order function to get neat error
// handling behavior
export function withFailureHandler<V>(extract: JSONValue => V): JSONValue => V {
  return json => {
    // Only initialize failureStack to an array here, as this allows `failure`
    // extractor to customize its logic based on whether the extraction has been
    // wrapped with this function or not.
    failureStack = [];
    try {
      const returnValue = extract(json);
      // Now `failureStack` is populated if any errors happened when extracting.
      //
      // Do not handle this on the server side, though, as we get better error
      // messages on browser side and debugging is also easier in the browser.
      // Failures will be handled again when React mounts, and debugging extractor
      // failures on browser-side is waaay easier than debugging an error
      // raised when rendering a component on the server side.
      if (failureStack.length > 0 && typeof document !== 'undefined') {
        const error = new Error('Extraction error');
        if (__DEV__) {
          // eslint-disable-next-line no-console
          console.error(`Extraction error:\n\n* ${failureStack.join('\n* ')}`, {
            json
          });
          // eslint-disable-next-line no-alert
          alert('Extraction error! Check console for more information');
        } else {
          Honeybadger.notify(error, {
            message: failureStack.join(', '),
            context: { json }
          });
        }
      }
      failureStack = null;
      return returnValue;
    } catch (e) {
      // Any errors that get here are result of real runtime errors, and we should
      // not handle them. We merely need to clear our caches.
      failureStack = null;
      throw e;
    }
  };
}

export function failure<V>(errorMessage: string, fallback: () => V): V {
  if (failureStack === null) {
    // This situation means that the extraction pipeline for this particular case
    // has not been wrapped with the failure handler function.
    //
    // In that case, handle the failures as before.
    const error = new Error(errorMessage);
    if (__DEV__) {
      throw error;
    } else {
      Honeybadger.notify(error);
      return fallback();
    }
  }
  // This extraction pipeline has been wrapped with a failure handling pipeline.
  // The failure handler will handle all errors received during extraction.
  failureStack.push(errorMessage);
  return fallback();
}
